<template>
  <section class="flex flex-col gap-2" v-if="r">
    <!-- Header -->
    <div
      class="p-4 px-8 bg-white rounded-md text-xl font-medium text-gray-700 text-center flex flex-row items-center lg:justify-between justify-center gap-2 flex-wrap"
    >
      <img src="@/assets/GamefamLogo.png" class="w-40 h-9 object-cover" alt="gamefam-logo" />
      {{ headerName }}
      <span class="text-sm text-gray-500">{{ headerDate }}</span>
    </div>

    <!-- Filters Used -->
    <div v-if="hasAnyFilters" class="p-4 bg-white rounded-md">
      <h2 class="text-base font-semibold mb-3">Filters Used</h2>

      <div class="filters-grid">
        <div
          v-for="group in filterGroups"
          :key="group.key"
          v-show="group.items.length"
          class="flex flex-col gap-2 min-w-0"
        >
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium">{{ group.label }}:</span>
            <span
              class="text-xs px-2 py-0.5 rounded-full"
              :class="group.include ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'"
              :title="group.include ? 'Only these are included' : 'These are excluded'"
            >
              {{ group.include ? 'Including' : 'Excluding' }}
            </span>
          </div>

          <div class="flex flex-wrap gap-2">
            <span
              v-for="(it, idx) in group.items"
              :key="`${group.key}-${idx}-${it}`"
              class="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
              :title="it"
            >
              {{ truncate(it, 28) }}
            </span>
          </div>
        </div>

        <div v-if="!anyListHasItems && anyIncludeFlagSet" class="text-sm text-gray-500 col-span-full">
          No filters applied.
        </div>
      </div>
    </div>

    <!-- Loading/empty -->
    <div v-if="!hasAnyData" class="flex flex-col gap-2 justify-center items-center h-full">
      <h2>No data to display</h2>
    </div>

    <!-- Summary cards -->
    <div v-if="hasAnyData" class="grid-container gap-2">
      <div class="card-container">
        <div class="inner-card">
          <label>Impressions Delivered</label>
          <span>{{ (r.impressions_delivered || 0).toLocaleString() }}</span>
        </div>
        <div class="inner-card">
          <label>Target Impressions</label>
          <span>{{ targetImpressions }}</span>
        </div>
      </div>

      <div class="card-container">
        <div class="inner-card">
          <label>Reach</label>
          <span>{{ (r.total_reach || 0).toLocaleString() }}</span>
        </div>
        <div class="inner-card">
          <label>Target Reach</label>
          <span>{{ targetReach }}</span>
        </div>
      </div>

      <div class="card-container">
        <div class="inner-card">
          <label>Avg. Frequency</label>
          <span>{{ numberOrDash(r.average_frequency, 2) }}</span>
        </div>
        <div class="inner-card">
          <label>Target Frequency</label>
          <span>{{ numberOrDash(r.target_frequency, 2) }}</span>
        </div>
      </div>

      <div class="card-container">
        <div class="inner-card">
          <label>Campaign Avg. View Time</label>
          <span>{{ r.avg_view_time ?? '...' }}</span>
        </div>
      </div>
    </div>

    <!-- Progress -->
    <div v-if="hasAnyData" class="p-4 bg-white rounded-md flex flex-col gap-4">
      <ProgressBar title="Current Pacing" :percentage="Math.floor(r.current_pacing_percentage || 0)" />
      <ProgressBar title="Target Pacing" :percentage="Math.floor(r.target_pacing_percentage || 0)" />
    </div>

    <!-- Charts -->
    <div v-if="hasImpOverTime" class="bg-white rounded-md p-2">
      <highcharts
        :options="impOverTimeOptions"
        :highcharts="Highcharts"
        :one-to-one="true"
        :update-flag="updateFlagIot"
        :update-args="[true, true, true]"
      />
    </div>

    <div v-if="hasExpBreakdown" class="bg-white rounded-md p-2">
      <highcharts
        :options="impPerExpOptions"
        :highcharts="Highcharts"
        :one-to-one="true"
        :update-flag="updateFlagExp"
        :update-args="[true, true, true]"
      />
    </div>

    <!-- Tables -->
    <div v-if="hasKeyStats" class="bg-white rounded-md p-2">
      <h1 class="text-center border-b-2 mb-2 p-4 text-lg font-bold">Key Campaign Stats</h1>
      <TableComponent
        :table_data="keyAdvertStatsTable"
        :exported_file_name="`${headerName}-key-campaign-stats`"
        :show_total_row="false"
        :show_average_row="false"
      />
    </div>

    <div v-if="hasExpBreakdown" class="bg-white rounded-md p-2">
      <h1 class="text-center border-b-2 mb-2 p-4 text-lg font-bold">Experience / Device Breakdown</h1>
      <TableComponent
        :table_data="expBreakdownTable"
        :exported_file_name="`${headerName}-experience-device-breakdown`"
        :show_total_row="false"
        :show_average_row="false"
      />
    </div>

    <div v-if="hasImpByDevice" class="bg-white rounded-md p-2">
      <h1 class="text-center border-b-2 mb-2 p-4 text-lg font-bold">Impressions By Device</h1>
      <TableComponent
        :table_data="impByDeviceTable"
        :exported_file_name="`${headerName}-impressions-by-device`"
        :show_total_row="false"
        :show_average_row="false"
      />
    </div>
  </section>
</template>

<script>
import ProgressBar from '@/components/UtilityComponents/ProgressBar.vue'
import TableComponent from '@/components/TableComponent.vue'
import { Chart as HighchartsVue } from 'highcharts-vue'
import Highcharts from 'highcharts'

const tooltipFormatter = function () {
  const fmt = this.series.chart.options.__impression_grouping === 'Daily'
    ? '%A, %B %e'
    : '%A, %B %e, %l:%M%P'
  return Highcharts.time.dateFormat(fmt, this.x) +
    '<br>' + this.series.name + ': <b>' + this.y.toLocaleString() + '</b>'
}

export default {
  name: 'NativeDisplayScheduledReportDisplay',
  props: { report: { type: Object, required: true } },
  components: { ProgressBar, TableComponent, highcharts: HighchartsVue },

  data: () => ({
    Highcharts,

    updateFlagIot: false,
    updateFlagExp: false,

    // Predefine three series with stable IDs to ensure Highcharts updates correctly.
    impOverTimeOptions: {
      chart: { zoomType: 'x' },
      title: { text: 'Impressions Over Time' },
      subtitle: { text: '' },
      plotOptions: { line: { connectNulls: false } },
      xAxis: { type: 'datetime', labels: { format: '{value: %b %e}' } },
      yAxis: { title: { text: 'Impressions' } },
      series: [
        { id: 'impressions', name: 'Impressions', type: 'line', data: [] },
        { id: 'unique',      name: 'Unique Impressions', type: 'line', data: [] },
        { id: 'renders',     name: 'Renders', type: 'line', data: [], visible: true }
      ],
      credits: { enabled: false },
      tooltip: { formatter: tooltipFormatter },
      __impression_grouping: 'Daily'
    },

    impPerExpOptions: {
      chart: { type: 'column' },
      title: { text: 'Impressions Per Experience' },
      xAxis: { categories: [] },
      yAxis: { title: { text: 'Impressions' }, labels: { overflow: 'justify' } },
      series: [],
      credits: { enabled: false }
    }
  }),

  computed: {
    r() {
      return (this.report && typeof this.report === 'object') ? this.report : null
    },

    // ---- NORMALIZED ARRAYS (parse JSON strings safely) ----
    iotArr()           { return this.ensureArray(this.r?.impressions_over_time) },
    rendersArr()       { return this.ensureArray(this.r?.renders_over_time) },
    expBreakdownArr()  { return this.ensureArray(this.r?.experience_device_breakdown) },
    keyAdvertStatsArr(){ return this.ensureArray(this.r?.key_advert_stats) },
    impByDeviceArr()   { return this.ensureArray(this.r?.impressions_by_device) },

    // ---- TABLES WITH FORCED COLUMN ORDER ----
    keyAdvertStatsTable() {
      const cols = ['Experience','Impressions','Reach','Frequency','Game Avg. View Time']
      const map = {
        Experience: ['Experience','experience','Game','game','Experience Name','friendly_name','name'],
        Impressions: ['Impressions','impressions','Delivered','delivered'],
        Reach: ['Reach','reach','Unique','unique'],
        Frequency: ['Frequency','frequency','Avg Frequency','avg_frequency','Average Frequency'],
        'Game Avg. View Time': ['Game Avg. View Time','game_avg_view_time','Avg. View Time','avg_view_time','Average View Time']
      }
      return this.toTable(this.keyAdvertStatsArr, cols, map)
    },

    expBreakdownTable() {
      const cols = ['Experience','Platform','Impressions','Reach','Frequency']
      const map = {
        Experience: ['Experience','experience','Game','game','friendly_name','name'],
        Platform: ['Platform','platform','Device','device'],
        Impressions: ['Impressions','impressions','Delivered','delivered'],
        Reach: ['Reach','reach','Unique','unique'],
        Frequency: ['Frequency','frequency','Avg Frequency','avg_frequency','Average Frequency']
      }
      return this.toTable(this.expBreakdownArr, cols, map)
    },

    impByDeviceTable() {
      const cols = ['Platform','Impressions','Reach','Frequency','Avg. View Time']
      const map = {
        Platform: ['Platform','platform','Device','device'],
        Impressions: ['Impressions','impressions','Delivered','delivered'],
        Reach: ['Reach','reach','Unique','unique'],
        Frequency: ['Frequency','frequency','Avg Frequency','avg_frequency','Average Frequency'],
        'Avg. View Time': ['Avg. View Time','avg_view_time','Average View Time']
      }
      return this.toTable(this.impByDeviceArr, cols, map)
    },

    // ---- guards now use normalized arrays ----
    hasAnyData() {
      if (!this.r) return false
      return Boolean(
        this.r.impressions_delivered ||
        this.keyAdvertStatsArr.length ||
        this.expBreakdownArr.length ||
        this.impByDeviceArr.length ||
        this.iotArr.length ||
        this.rendersArr.length
      )
    },
    hasImpOverTime()   { return (this.iotArr.length + this.rendersArr.length) > 0 },
    hasExpBreakdown()  { return this.expBreakdownArr.length > 0 },
    hasImpByDevice()   { return this.impByDeviceArr.length > 0 },
    hasKeyStats()      { return this.keyAdvertStatsArr.length > 0 },

    headerName() {
      return this.r?.campaign?.name || this.r?.title || 'Report'
    },
    headerDate() {
      const start = this.r?.date_range?.start
      const end   = this.r?.date_range?.end
      if (!start || !end) return ''
      return ` From ${this.formatYMD(start)} To ${this.formatYMD(end)}`
    },
    targetImpressions() {
      const val = this.r?.target_impressions
      return val == null ? '...' : Number(val).toLocaleString()
    },
    targetReach() {
      const val = this.r?.target_reach
      return val == null ? '...' : Number(val).toLocaleString()
    },

    filters() { return this.r?.filters || {} },

    filterGroups() {
      const pluckStrings = (arr, keySelector) => {
        if (!Array.isArray(arr)) return []
        const out = arr.map(item => {
          if (item == null) return ''
          if (typeof item === 'string') return item
          if (typeof item === 'number') return String(item)
          if (typeof item === 'object') {
            const val = keySelector(item)
            return typeof val === 'string' ? val : ''
          }
          return ''
        }).filter(Boolean)
        return [...new Set(out)]
      }

      const countries = pluckStrings(this.filters.countries, c => c.name ?? '')
      const runs      = pluckStrings(this.filters.runs,      r => r.name ?? '')
      const games     = pluckStrings(this.filters.games,     g => g.friendly_name ?? g.name ?? '')

      return [
        { key: 'countries', label: 'Countries', items: countries, include: !!this.filters.include_countries },
        { key: 'runs',      label: 'Runs',      items: runs,      include: !!this.filters.include_runs },
        { key: 'games',     label: 'Games',     items: games,     include: !!this.filters.include_games }
      ]
    },

    anyListHasItems()   { return this.filterGroups.some(g => g.items.length) },
    anyIncludeFlagSet() { return ['include_countries','include_runs','include_games'].some(k => !!this.filters?.[k]) },
    hasAnyFilters()     { return this.anyListHasItems || this.anyIncludeFlagSet }
  },

  watch: {
    report: {
      handler(newVal) {
        if (!newVal) return
        this.rebuildCharts(newVal)
      },
      immediate: true,
      deep: true
    }
  },

  mounted() {
    if (this.r) this.rebuildCharts(this.r)
  },

  methods: {
    // Convert possible JSON string → array. Anything else → [].
    ensureArray(maybe) {
      if (Array.isArray(maybe)) return maybe
      if (typeof maybe === 'string') {
        const trimmed = maybe.trim()
        if (!trimmed) return []
        try {
          const parsed = JSON.parse(trimmed)
          return Array.isArray(parsed) ? parsed : []
        } catch (e) {
          console.warn('Failed to parse JSON array:', e, maybe)
          return []
        }
      }
      return []
    },

    // Build a table with exact column order using candidate key aliases
    toTable(rows, cols, aliasMap) {
      if (!Array.isArray(rows) || !rows.length) return []
      const normKey = k => String(k).toLowerCase().replace(/\s+|_/g, '')
      const findVal = (row, aliases) => {
        for (const a of aliases) {
          if (row[a] != null) return row[a]
          const direct = row[a?.toString?.()]
          if (direct != null) return direct
          // try lenient lookups (case/space/underscore-insensitive)
          const target = normKey(a)
          for (const key of Object.keys(row)) {
            if (normKey(key) === target) return row[key]
          }
        }
        return null
      }
      return rows.map(r => {
        const out = {}
        for (const col of cols) out[col] = findVal(r, aliasMap[col] || [col])
        return out
      })
    },

    // Parse to UTC ms: supports YYYY-MM-DD[ HH[:mm[:ss]]]
    parseToUtcMillis(s) {
      if (!s) return NaN
      const str = String(s).trim()
      const m = str.match(
        /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2})(?::(\d{2})(?::(\d{2}))?)?)?$/
      )
      if (!m) {
        const t = Date.parse(str)
        return Number.isFinite(t) ? t : NaN
      }
      const y  = Number(m[1])
      const mo = Number(m[2]) - 1
      const d  = Number(m[3])
      const H  = m[4] != null ? Number(m[4]) : 0
      const M  = m[5] != null ? Number(m[5]) : 0
      const S  = m[6] != null ? Number(m[6]) : 0
      return Date.UTC(y, mo, d, H, M, S)
    },

    rebuildCharts(rep) {
      const iot     = this.ensureArray(rep.impressions_over_time)
      const renders = this.ensureArray(rep.renders_over_time)

      const toPoint = (row, yKey) => {
        const t = this.parseToUtcMillis(row.Time || row.time || row.date || row.timestamp)
        if (!Number.isFinite(t)) return null
        const cand = [yKey, yKey?.toLowerCase?.(), yKey?.replace?.(' ', '_')?.toLowerCase?.()]
        let y = 0
        for (const k of cand) {
          if (k != null && row[k] != null) { y = Number(row[k]); break }
        }
        return [t, Number.isFinite(y) ? y : 0]
      }

      const impData    = iot.map(r => toPoint(r, 'Impressions')).filter(Boolean)
      const uniqData   = iot.map(r => toPoint(r, 'Unique Impressions')).filter(Boolean)
      const renderData = renders.map(r => toPoint(r, 'Renders')).filter(Boolean)

      this.impOverTimeOptions.subtitle.text = rep.user_timezone ? `Timezone: ${rep.user_timezone}` : 'Timezone:'
      this.impOverTimeOptions.__impression_grouping = rep.impression_grouping || 'Daily'

      const sImpressions = this.impOverTimeOptions.series.find(s => s.id === 'impressions')
      const sUnique      = this.impOverTimeOptions.series.find(s => s.id === 'unique')
      const sRenders     = this.impOverTimeOptions.series.find(s => s.id === 'renders')

      if (sImpressions) sImpressions.data = impData
      if (sUnique)      sUnique.data      = uniqData
      if (sRenders) {
        sRenders.data    = renderData
        sRenders.visible = renderData.length > 0
      }

      this.updateFlagIot = !this.updateFlagIot

      // Experience / device: aggregate by Experience for chart only
      const exp = this.ensureArray(rep.experience_device_breakdown)
      const byExp = {}
      for (const e of exp) {
        const label = e.Experience ?? e.experience ?? 'Experience'
        const val = Number(e.Impressions ?? e.impressions ?? 0)
        byExp[label] = (byExp[label] || 0) + (Number.isFinite(val) ? val : 0)
      }
      const categories = Object.keys(byExp)
      const data = categories.map(k => byExp[k])

      this.impPerExpOptions.xAxis.categories = categories
      this.impPerExpOptions.series.splice(0, this.impPerExpOptions.series.length, { name: 'Impressions', data })
      this.updateFlagExp = !this.updateFlagExp
    },

    numberOrDash(val, decimals = 2) {
      if (val == null || val === '') return '...'
      const n = Number(val)
      return Number.isFinite(n) ? n.toFixed(decimals) : '...'
    },

    formatYMD(d) {
      const m = String(d || '').match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (!m || m[2] === '00' || m[3] === '00') return ''
      const dt = new Date(Date.UTC(+m[1], +m[2]-1, +m[3]))
      return Number.isNaN(dt.getTime()) ? '' : dt.toISOString().slice(0, 10)
    },

    truncate(s, max = 28) {
      if (!s) return ''
      return s.length <= max ? s : s.slice(0, max - 1) + '…'
    }
  }
}
</script>

<style lang="postcss" scoped>
.grid-container { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); }
.card-container { @apply bg-white rounded-md flex flex-col items-center justify-center p-4 gap-4; }
.inner-card { @apply flex flex-col gap-2 items-center; }
.card-container span { @apply text-3xl font-bold; }

/* Filters grid wraps horizontally and stacks into columns when needed */
.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 0.75rem; /* ~ gap-3 */
}
</style>